'use client';

import React, { forwardRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn } from '../../utils/cn';

const Dropdown: React.FC<React.ComponentProps<typeof DropdownMenu.Root>> = DropdownMenu.Root;

const DropdownTrigger: React.FC<React.ComponentProps<typeof DropdownMenu.Trigger>> = DropdownMenu.Trigger;

const DropdownContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    sideOffset?: number;
  }
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenu.Portal>
    <DropdownMenu.Content
      ref={ref as any}
      sideOffset={sideOffset}
      asChild
      {...(props as any)}
    >
      <div
        className={cn(
          'z-50 min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white p-1 text-gray-950 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      />
    </DropdownMenu.Content>
  </DropdownMenu.Portal>
));
DropdownContent.displayName = DropdownMenu.Content.displayName;

const DropdownItem = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    inset?: boolean;
    disabled?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenu.Item
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        inset && 'pl-8',
        className
      )}
    >
      {children}
    </div>
  </DropdownMenu.Item>
));
DropdownItem.displayName = DropdownMenu.Item.displayName;

const DropdownCheckboxItem = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    checked?: boolean;
  }
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenu.CheckboxItem
    ref={ref as any}
    asChild
    checked={checked}
    {...(props as any)}
  >
    <div
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </div>
  </DropdownMenu.CheckboxItem>
));
DropdownCheckboxItem.displayName = DropdownMenu.CheckboxItem.displayName;

const DropdownRadioItem = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <DropdownMenu.RadioItem
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <DropdownMenu.ItemIndicator>
          <svg className="h-2 w-2 fill-current" viewBox="0 0 8 8">
            <circle cx={4} cy={4} r={2} />
          </svg>
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </div>
  </DropdownMenu.RadioItem>
));
DropdownRadioItem.displayName = DropdownMenu.RadioItem.displayName;

const DropdownLabel = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenu.Label
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn(
        'px-2 py-1.5 text-sm font-semibold',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  </DropdownMenu.Label>
));
DropdownLabel.displayName = DropdownMenu.Label.displayName;

const DropdownSeparator = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <DropdownMenu.Separator
    ref={ref as any}
    asChild
    {...(props as any)}
  >
    <div
      className={cn('-mx-1 my-1 h-px bg-gray-100', className)}
      {...props}
    />
  </DropdownMenu.Separator>
));
DropdownSeparator.displayName = DropdownMenu.Separator.displayName;

const DropdownGroup: React.FC<React.ComponentProps<typeof DropdownMenu.Group>> = DropdownMenu.Group;
const DropdownPortal: React.FC<React.ComponentProps<typeof DropdownMenu.Portal>> = DropdownMenu.Portal;
const DropdownSub: React.FC<React.ComponentProps<typeof DropdownMenu.Sub>> = DropdownMenu.Sub;
const DropdownSubContent: React.FC<React.ComponentProps<typeof DropdownMenu.SubContent>> = DropdownMenu.SubContent;
const DropdownSubTrigger: React.FC<React.ComponentProps<typeof DropdownMenu.SubTrigger>> = DropdownMenu.SubTrigger;
const DropdownRadioGroup: React.FC<React.ComponentProps<typeof DropdownMenu.RadioGroup>> = DropdownMenu.RadioGroup;

// Simple shortcut component
const DropdownShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
      {...props}
    />
  );
};
DropdownShortcut.displayName = 'DropdownShortcut';

export {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownCheckboxItem,
  DropdownRadioItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownShortcut,
  DropdownGroup,
  DropdownPortal,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
  DropdownRadioGroup,
};
